import { PageController } from '@client/components/_core/layout/PageController';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Card } from 'antd';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { z } from 'zod';

/**
 * Where Microsoft returns a tenancy's admin after the admin consent link for compliance audit
 * document sources (CUR-2565; `consentUrl` in `DocumentSourceConnections`). The admin is usually an
 * authorised representative's, not a Curium user, so the page is public and records nothing: Curium
 * proves consent by signing in to the tenancy when the connection is tested, never from this
 * redirect. Anyone can open it with any query, so a granted answer is shown as what Microsoft
 * reported, not as confirmed. Microsoft's `error_description` is not shown, only a known error
 * code's own wording.
 */
const searchSchema = z.object({
    admin_consent: z.string().max(10).optional(),
    error: z.string().max(100).optional(),
});

const CONSENT_DECLINED = {
    title: 'Consent declined',
    description:
        'Consent was not granted, so Curium cannot read any of this tenancy’s sites. You can open the consent link again to grant it.',
};

/** Keyed by Microsoft's `error` code: `permission_denied` is an admin declining or cancelling the consent. */
const ERROR_MESSAGES: Record<string, { title: string; description: string }> = {
    access_denied: CONSENT_DECLINED,
    permission_denied: CONSENT_DECLINED,
    unauthorized_client: {
        title: 'Application not allowed',
        description:
            'This tenancy does not allow the Curium compliance audit application. Check your application consent policies, then open the consent link again.',
    },
    invalid_request: {
        title: 'Consent link not valid',
        description: 'Microsoft could not read the consent link. Ask the organisation that sent it for a new one.',
    },
    server_error: {
        title: 'Microsoft 365 did not answer',
        description: 'Microsoft could not complete the consent. Try the consent link again in a few minutes.',
    },
    temporarily_unavailable: {
        title: 'Microsoft 365 did not answer',
        description: 'Microsoft could not complete the consent. Try the consent link again in a few minutes.',
    },
};

const UNKNOWN_ERROR = {
    title: 'Consent not completed',
    description:
        'Microsoft did not complete the consent. Check that you signed in as an administrator of the tenancy, then open the consent link again.',
};

export const Route = createFileRoute('/document-sources/consent')({
    validateSearch: zodValidator(searchSchema),
    component: DocumentSourceConsent,
});

function DocumentSourceConsent() {
    const { admin_consent, error } = Route.useSearch();
    const granted = admin_consent?.toLowerCase() === 'true' && !error;
    // Own keys only: `?error=constructor` must not find an `Object.prototype` member.
    const failure = error && Object.hasOwn(ERROR_MESSAGES, error) ? ERROR_MESSAGES[error] : UNKNOWN_ERROR;

    return (
        <PageController route={Route}>
            <div className="min-h-screen w-full bg-muted">
                <div className="flex min-h-screen items-center justify-center">
                    <div className="w-full max-w-lg px-4">
                        <Card>
                            <div className="text-center">
                                <div className="mb-6">
                                    <img
                                        src="/static/curium-logo.svg"
                                        alt="Curium logo"
                                        className="mx-auto h-16 w-auto"
                                    />
                                </div>
                                {granted ? (
                                    <>
                                        <div className="mb-6 flex justify-center">
                                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-success-subtle">
                                                <CheckCircle className="h-10 w-10 text-success" />
                                            </div>
                                        </div>
                                        {/* curium:allow-heading — Microsoft admin consent landing page, no app shell */}
                                        <h2 className="mb-4 font-semibold text-2xl text-foreground">
                                            Consent response received
                                        </h2>
                                        <p className="mb-6 text-base text-muted-foreground">
                                            Microsoft sent you back here reporting that consent was granted to the
                                            Curium compliance audit application. This page cannot check that: Curium
                                            confirms the consent when the organisation that sent you the link tests the
                                            connection. The application can read only the sites you grant it.
                                        </p>
                                        <div className="mb-2 rounded-lg bg-info-subtle p-4 text-left">
                                            {/* curium:allow-heading — Microsoft admin consent landing page, no app shell */}
                                            <h3 className="mb-2 font-semibold text-info-foreground">Next steps</h3>
                                            <ol className="list-decimal space-y-1 pl-5 text-info-foreground text-sm">
                                                <li>
                                                    Run the grant script you were sent for each site Curium should read.
                                                </li>
                                                <li>
                                                    Tell the organisation that sent you the consent link. They test the
                                                    connection in Curium, which confirms the consent and the site
                                                    grants.
                                                </li>
                                            </ol>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="mb-6 flex justify-center">
                                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-warning-subtle">
                                                <AlertCircle className="h-10 w-10 text-warning" />
                                            </div>
                                        </div>
                                        {/* curium:allow-heading — Microsoft admin consent landing page, no app shell */}
                                        <h2 className="mb-4 font-semibold text-2xl text-foreground">{failure.title}</h2>
                                        <p className="mb-2 text-base text-muted-foreground">{failure.description}</p>
                                    </>
                                )}
                                <p className="mt-6 text-muted-foreground text-sm">You can close this window.</p>
                            </div>
                        </Card>
                        <div className="mt-6 text-center">
                            <p className="text-sm text-subtle-foreground">
                                Need help?{' '}
                                <a
                                    href="mailto:support@getcurium.com"
                                    className="font-medium text-primary-600 hover:text-primary-700"
                                >
                                    Contact support
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </PageController>
    );
}
